import React, { useState, useEffect, useContext } from "react";
import { NavLink } from "react-router-dom";
import { ArrowUp } from "lucide-react";
import LogoSvg from "../assets/svg/logos/logo.svg";
import { AuthContext } from "../context/AuthContext";
import FooterPrivacyImg from "../assets/images/footer/DataPrivacy.svg";
import FooterSpamImg from "../assets/images/footer/zeroSpam.svg";
import FooterRatingImg from "../assets/images/footer/userRating.svg";
import FooterSslSecuredImg from "../assets/images/footer/sslSecured.svg";

const Footer = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { isAuthenticated } = useContext(AuthContext);

  // Toggle top function button visibility based on scroll position
  useEffect(() => {
    const toggleVisibility = () => {
      if (window.pageYOffset > 300) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };
    window.addEventListener("scroll", toggleVisibility);
    return () => window.removeEventListener("scroll", toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const year = new Date().getFullYear();

  return (
    <>
      <footer className="tw-bg-slate-950 tw-text-slate-400 tw-font-sans tw-border-t tw-border-slate-900">
        <div className="tw-max-w-7xl tw-mx-auto tw-px-6 sm:tw-px-8 lg:tw-px-10 tw-py-16">
          {/* Main Footer Columns */}
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 lg:tw-grid-cols-6 tw-gap-10 tw-mb-16">
            {/* Brand and Description Column */}
            <div className="tw-flex tw-flex-col tw-text-left md:tw-col-span-4 lg:tw-col-span-2 tw-pr-0 lg:tw-pr-8">
              <NavLink
                to={isAuthenticated ? "/dashboard-view" : "/"}
                className="tw-flex tw-items-center tw-mb-5 hover:tw-opacity-95 tw-transition-opacity"
              >
                <img
                  src={LogoSvg}
                  alt="BeyondLeadz"
                  className="!tw-h-8 tw-w-auto tw-object-contain"
                />
              </NavLink>
              <p className="tw-text-sm tw-leading-relaxed tw-text-slate-400 tw-mb-6 tw-max-w-sm">
                Streamline your lead management, automate sales operations, and
                build a stellar online reputation with our intelligent,
                hospitality-focused sales automation platform.
              </p>

              {/* Social icons */}
              <div className="tw-flex tw-items-center tw-gap-3">
                <a
                  href="#facebook"
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-slate-900 tw-border tw-border-slate-800 tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-bg-[#5D44FF] hover:tw-text-white hover:tw-border-[#5D44FF] tw-transition-all tw-duration-300 hover:tw-scale-105"
                  aria-label="Facebook"
                >
                  <svg
                    className="tw-w-4 tw-h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c4.56-.93 8-4.96 8-9.75z" />
                  </svg>
                </a>

                <a
                  href="https://x.com/beyond_leadz"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-slate-900 tw-border tw-border-slate-800 tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-bg-[#5D44FF] hover:tw-text-white hover:tw-border-[#5D44FF] tw-transition-all tw-duration-300 hover:tw-scale-105"
                  aria-label="X (formerly Twitter)"
                >
                  <svg
                    className="tw-w-4 tw-h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>

                <a
                  href="https://www.linkedin.com/company/beyondleaz/"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-slate-900 tw-border tw-border-slate-800 tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-bg-[#5D44FF] hover:tw-text-white hover:tw-border-[#5D44FF] tw-transition-all tw-duration-300 hover:tw-scale-105"
                  aria-label="LinkedIn"
                >
                  <svg
                    className="tw-w-4 tw-h-4"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
                  </svg>
                </a>

                <a
                  href="https://www.instagram.com/beyond_leadz/"
                  target="_blank"
                  rel="noreferrer"
                  className="tw-w-10 tw-h-10 tw-rounded-xl tw-bg-slate-900 tw-border tw-border-slate-800 tw-flex tw-items-center tw-justify-center tw-text-slate-400 hover:tw-bg-[#5D44FF] hover:tw-text-white hover:tw-border-[#5D44FF] tw-transition-all tw-duration-300 hover:tw-scale-105"
                  aria-label="Instagram"
                >
                  <svg
                    className="tw-w-4 tw-h-4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                  </svg>
                </a>
              </div>
            </div>

            {/* Solutions Column */}
            <div className="tw-flex tw-flex-col tw-text-left">
              <h3 className="tw-text-xs tw-font-extrabold tw-tracking-widest tw-text-white tw-uppercase tw-mb-5">
                Solutions
              </h3>
              <ul className="tw-space-y-3 tw-list-none tw-p-0">
                <li>
                  <NavLink
                    to="/b2b-data"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    B2B Data
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/lead-management"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Lead Management
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/review-management"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Review Management
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/lead-enrichment"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Lead Enrichment
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Company Column */}
            <div className="tw-flex tw-flex-col tw-text-left">
              <h3 className="tw-text-xs tw-font-extrabold tw-tracking-widest tw-text-white tw-uppercase tw-mb-5">
                Company
              </h3>
              <ul className="tw-space-y-3 tw-list-none tw-p-0">
                <li>
                  <NavLink
                    to="/about-us"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    About us
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/career"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Career
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/contact-us"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Contact us
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Support Column */}
            <div className="tw-flex tw-flex-col tw-text-left">
              <h3 className="tw-text-xs tw-font-extrabold tw-tracking-widest tw-text-white tw-uppercase tw-mb-5">
                Support
              </h3>
              <ul className="tw-space-y-3 tw-list-none tw-p-0">
                <li>
                  <NavLink
                    to="/features"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Features
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/linkedin-extension"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Extension
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/faq"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Faqs
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/pricing"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Pricing
                  </NavLink>
                </li>
                <li>
                  <a
                    href="#blog"
                    className="tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact Column */}
            <div className="tw-flex tw-flex-col tw-text-left">
              <h3 className="tw-text-xs tw-font-extrabold tw-tracking-widest tw-text-white tw-uppercase tw-mb-5">
                Contact
              </h3>
              <ul className="tw-space-y-4 tw-list-none tw-p-0">
                <li>
                  <a
                    href="mailto:contact@beyondleadz.ai"
                    className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200 tw-group"
                  >
                    <svg
                      className="tw-w-4 tw-h-4 tw-flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    contact@beyondleadz.ai
                  </a>
                </li>
                <li>
                  <a
                    href="tel:+91-9625-981200"
                    className="tw-flex tw-items-center tw-gap-2.5 tw-text-sm tw-text-slate-400 hover:tw-text-[#5D44FF] tw-transition-colors tw-duration-200"
                  >
                    <svg
                      className="tw-w-4 tw-h-4 tw-flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.94.725l.548 2.2a1 1 0 01-.321.988l-1.305.98a10.582 10.582 0 004.872 4.872l.98-1.305a1 1 0 01.988-.321l2.2.548a1 1 0 01.725.94V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                    +91-9625-981200
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Trust Certificates Badge Grid */}
          <div className="tw-bg-slate-900/30 tw-border tw-border-slate-900/60 tw-rounded-3xl tw-p-8 tw-mb-12">
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-6 tw-items-center">
              <div className="tw-flex tw-justify-center">
                <img
                  src={FooterPrivacyImg}
                  alt="GDPR Compliant"
                  className="tw-h-14 tw-object-contain tw-opacity-80 hover:tw-opacity-100 tw-transition-opacity"
                />
              </div>
              <div className="tw-flex tw-justify-center">
                <img
                  src={FooterSpamImg}
                  alt="Spam Safe"
                  className="tw-h-14 tw-object-contain tw-opacity-80 hover:tw-opacity-100 tw-transition-opacity"
                />
              </div>
              <div className="tw-flex tw-justify-center">
                <img
                  src={FooterRatingImg}
                  alt="Top Customer Rated"
                  className="tw-h-14 tw-object-contain tw-opacity-80 hover:tw-opacity-100 tw-transition-opacity"
                />
              </div>
              <div className="tw-flex tw-justify-center">
                <img
                  src={FooterSslSecuredImg}
                  alt="SSL Encrypted"
                  className="tw-h-14 tw-object-contain tw-opacity-80 hover:tw-opacity-100 tw-transition-opacity"
                />
              </div>
            </div>
          </div>

          {/* Bottom Copyright Row */}
          <div className="tw-flex tw-flex-col sm:tw-flex-row tw-items-center tw-justify-between tw-gap-4 tw-pt-4 tw-border-t tw-border-slate-900">
            <p className="tw-text-xs tw-text-slate-500">
              © {year} BeyondLeadz. All rights reserved.
            </p>
            <div className="tw-flex tw-gap-6 tw-flex-wrap">
              <NavLink
                to="/privacy-policy"
                className="tw-text-xs tw-text-slate-500 hover:tw-text-slate-400"
              >
                Privacy Policy
              </NavLink>
              <NavLink
                to="/security-policy"
                className="tw-text-xs tw-text-slate-500 hover:tw-text-slate-400"
              >
                Security Policy
              </NavLink>
              <NavLink
                to="/terms-and-conditions"
                className="tw-text-xs tw-text-slate-500 hover:tw-text-slate-400"
              >
                Terms & Conditions
              </NavLink>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating scroll-to-top button */}
      {isVisible && (
        <button
          onClick={scrollToTop}
          className="tw-fixed tw-bottom-20 tw-left-8 md:tw-left-auto md:tw-right-8 md:tw-bottom-48 tw-z-50 tw-bg-white hover:tw-bg-indigo-50/80 tw-text-[#5D44FF] tw-w-12 tw-h-12 tw-rounded-full tw-shadow-xl tw-border tw-border-solid tw-border-slate-200/80 tw-flex tw-items-center tw-justify-center tw-transition-all tw-duration-300 hover:tw-scale-110 tw-outline-none tw-cursor-pointer"
          title="Go to top"
        >
          <ArrowUp className="tw-w-5 tw-h-5 tw-text-[#5D44FF]" />
        </button>
      )}
    </>
  );
};

export default Footer;
